import React, { useEffect, useCallback, useState, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  Connection,
  MiniMap,
  Background,
  BackgroundVariant,
  useReactFlow,
  useViewport,
} from 'reactflow';
import LayoutControls from './LayoutControls';
import { useIsMobile } from '../../hooks/useIsMobile';
import { LayoutDirection } from '../../utils/layoutEngine';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import { useThemeStore } from '../../store/themeStore';

interface FlowContentProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  onNodeClick: (event: React.MouseEvent, node: Node) => void;
  onNodeContextMenu: (event: React.MouseEvent, node: Node) => void;
  onEdgeClick: (event: React.MouseEvent, edge: Edge) => void;
  onDragOver: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
  onNodeDragStop?: (event: React.MouseEvent, node: Node, nodes: Node[]) => void;
  nodeTypes: any;
  edgeTypes: any;
  defaultEdgeOptions: any;
  snapToGridEnabled: boolean;
  gridSize: number;
  gridEnabled: boolean;
  onAutoLayout: (direction: LayoutDirection) => void;
  onCompactLayout: () => void;
  onAlignNodes: (alignment: 'left' | 'right' | 'center' | 'top' | 'bottom' | 'middle') => void;
  onDistributeNodes: (direction: 'horizontal' | 'vertical') => void;
  onToggleGrid: () => void;
  onToggleSnapToGrid: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  selectedNodesCount: number;
  workflowId?: string;
}

const FlowContent: React.FC<FlowContentProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onNodeContextMenu,
  onEdgeClick,
  onDragOver,
  onDrop,
  onNodeDragStop,
  nodeTypes,
  edgeTypes,
  defaultEdgeOptions,
  snapToGridEnabled,
  gridSize,
  gridEnabled,
  onAutoLayout,
  onCompactLayout,
  onAlignNodes,
  onDistributeNodes,
  onToggleGrid,
  onToggleSnapToGrid,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  selectedNodesCount,
  workflowId,
}) => {
  const { fitView, zoomIn, zoomOut, setViewport } = useReactFlow();
  const isDark = useThemeStore((s) => s.resolvedTheme) === 'dark';
  const isMobile = useIsMobile();
  const { zoom } = useViewport();
  const [selectionMode, setSelectionMode] = useState(false);
  const [isCanvasLocked, setIsCanvasLocked] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const fitViewMaxZoom = isMobile ? 0.75 : 1;

  const handleToggleCanvasLock = useCallback(() => {
    setIsCanvasLocked(prev => !prev);
  }, []);

  const handleResetZoom = useCallback(() => {
    setViewport({ x: 0, y: 0, zoom: fitViewMaxZoom }, { duration: 300 });
  }, [setViewport, fitViewMaxZoom]);

  // Auto-fit only on load / structural changes — NOT on every node mutation
  // (dragging or selecting a node changes the `nodes` identity but must not
  // re-center the canvas). We key the fit on workflowId + node count so the
  // effect can depend on `nodes` (to read fresh positions for the finiteness
  // check below) without re-firing on position-only changes.
  const lastFitKeyRef = useRef<string | null>(null);
  // AC-1694: clear the fit-key when the workflow changes so the canvas
  // re-frames the NEW workflow even if it has the same node count as the
  // previous one. Otherwise the key (workflowId:nodes.length) could match the
  // count fit while the prior workflow's nodes were still showing and fitView
  // would be skipped, leaving the viewport framed on the old workflow.
  useEffect(() => {
    lastFitKeyRef.current = null;
  }, [workflowId]);
  useEffect(() => {
    if (!workflowId || nodes.length === 0) return;
    const fitKey = `${workflowId}:${nodes.length}`;
    if (lastFitKeyRef.current === fitKey) return;
    // Small delay to ensure nodes are rendered
    const timer = setTimeout(() => {
      // AC-1626: fitView cannot frame NaN/Infinity-positioned nodes — it
      // yields a NaN zoom that propagates to the indicator ("NaN%"). Only
      // fit when every node has finite coordinates; otherwise fall back to
      // a fixed default viewport so the zoom stays finite.
      const allFinite = nodes.every(
        (n) => Number.isFinite(n.position?.x) && Number.isFinite(n.position?.y)
      );
      if (allFinite) {
        fitView({ padding: 0.1, includeHiddenNodes: false, duration: 800, maxZoom: fitViewMaxZoom });
      } else {
        setViewport({ x: 0, y: 0, zoom: fitViewMaxZoom });
      }
      lastFitKeyRef.current = fitKey;
    }, 100);
    return () => clearTimeout(timer);
  }, [workflowId, nodes, fitView, setViewport, fitViewMaxZoom]);

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.1, duration: 800, maxZoom: fitViewMaxZoom });
  }, [fitView, fitViewMaxZoom]);

  // Keyboard shortcut for fit view
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if user is typing in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Skip shortcuts when focus is inside a modal, dialog, or dropdown
      const target = e.target as HTMLElement;
      if (target.closest('[role="dialog"], [role="listbox"], [data-modal-active="true"]')) {
        return;
      }

      // Ctrl/Cmd + 0: Fit view
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        handleFitView();
      }

      // S key: Toggle selection mode
      if (e.key === 's' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setSelectionMode(prev => !prev);
      }

      // "?" key (or Shift+/) - Show keyboard shortcuts help
      if ((e.key === '?' || (e.key === '/' && e.shiftKey)) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setShowShortcutsHelp(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleFitView]);

  return (
    <>
      {/* Selection Mode Indicator - positioned at bottom left */}
      {selectionMode && (
        <div className="absolute bottom-4 left-4 z-20 bg-blue-500 text-white px-3 py-1.5 rounded-md shadow-lg flex items-center gap-2 text-sm">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h3m13 0h-3m-7 0h7m-13 5h3m10 0h3m-16 5h3m13 0h-3m-7 0h7M5 5v3m0 4v3m0 4v3m14-14v3m0 4v3m0 4v3" />
          </svg>
          <span>Selection Mode</span>
          <span className="opacity-75 text-xs">(Press S to exit)</span>
        </div>
      )}
      
      <LayoutControls
        onAutoLayout={onAutoLayout}
        onCompactLayout={onCompactLayout}
        onAlignNodes={onAlignNodes}
        onDistributeNodes={onDistributeNodes}
        onToggleGrid={onToggleGrid}
        onToggleSnapToGrid={onToggleSnapToGrid}
        onFitView={handleFitView}
        onUndo={onUndo}
        onRedo={onRedo}
        onToggleSelectionMode={() => setSelectionMode(!selectionMode)}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onToggleCanvasLock={handleToggleCanvasLock}
        zoomLevel={zoom}
        onResetZoom={handleResetZoom}
        gridEnabled={gridEnabled}
        snapToGrid={snapToGridEnabled}
        selectionMode={selectionMode}
        canUndo={canUndo}
        canRedo={canRedo}
        isCanvasLocked={isCanvasLocked}
        selectedNodesCount={selectedNodesCount}
      />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onNodeContextMenu={onNodeContextMenu}
        onEdgeClick={onEdgeClick}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onNodeDragStop={onNodeDragStop}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        fitViewOptions={{ maxZoom: fitViewMaxZoom }}
        attributionPosition="bottom-right"
        className="bg-gray-50 dark:bg-gray-900"
        snapToGrid={snapToGridEnabled}
        snapGrid={[gridSize, gridSize]}
        panOnDrag={!selectionMode && !isCanvasLocked}
        selectionOnDrag={selectionMode}
        nodesDraggable={!isCanvasLocked}
        nodesConnectable={!isCanvasLocked}
        elementsSelectable={!isCanvasLocked}
        nodeDragThreshold={5}
      >
        {!isMobile && (
          <MiniMap
            nodeStrokeColor={(n) => {
              if (n.type === 'agent') return isDark ? '#2563eb' : '#3b82f6';
              if (n.type === 'conditional') return isDark ? '#d97706' : '#f59e0b';
              if (n.type === 'queueWrite') return isDark ? '#0d9488' : '#14b8a6';
              if (n.type === 'queueRead') return isDark ? '#7c3aed' : '#9333ea';
              return isDark ? '#4b5563' : '#6b7280';
            }}
            nodeColor={(n) => {
              if (n.type === 'agent') return isDark ? '#1e3a5f' : '#dbeafe';
              if (n.type === 'conditional') return isDark ? '#422006' : '#fef3c7';
              if (n.type === 'queueWrite') return isDark ? '#042f2e' : '#ccfbf1';
              if (n.type === 'queueRead') return isDark ? '#2e1065' : '#e9d5ff';
              return isDark ? '#1f2937' : '#f3f4f6';
            }}
            nodeBorderRadius={8}
            maskColor={isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(240, 240, 240, 0.6)'}
          />
        )}
        {gridEnabled && (
          <Background
            variant={BackgroundVariant.Dots}
            gap={gridSize}
            size={2}
            color={isDark ? '#4b5563' : '#c0c0c0'}
          />
        )}
      </ReactFlow>
      <KeyboardShortcutsHelp
        isOpen={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
      />
    </>
  );
};

export default FlowContent;